import React, { useState, useEffect, useRef, useCallback } from 'react';
import StarRating from '../StarRating';
import './WriteReviewModal.css';

/**
 * WriteReviewModal - Modal for writing event reviews
 *
 * Features:
 * - Title input (optional)
 * - Role selection (Attendee, Volunteer, Artist, Guest)
 * - 6 rating categories with star inputs
 * - Review text with word count validation (20-100 words)
 * - Cultural discussions toggle
 * - Private organizer recommendations
 * - Swear word filter
 */
const WriteReviewModal = ({
  isOpen,
  onClose,
  eventId,
  eventTitle,
  eventArtists = [],
  onSuccess,
  onOpenBoycott,
  apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'
}) => {
  // Form state
  const [title, setTitle] = useState('');
  const [selectedRole, setSelectedRole] = useState('attendee');
  const [hideRole, setHideRole] = useState(false);
  const [reviewText, setReviewText] = useState('');
  const [eventAmenities, setEventAmenities] = useState({
    codeOfConduct: false,
    freeWater: false,
    freeSnacks: false,
    culturalDiscussions: false,
    switchHour: false,
    socialActivities: false,
  });
  const [organizerRecommendations, setOrganizerRecommendations] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showExtraInfo, setShowExtraInfo] = useState(false);
  const [nameWarningVisible, setNameWarningVisible] = useState(false);
  const [responsibilityConfirmed, setResponsibilityConfirmed] = useState(false);

  // Ratings state
  const [ratings, setRatings] = useState({
    venue: 0,
    ratio: 0,
    organization: 0,
    artists: 0,
    culture: 0,
    safety: 0
  });

  const modalRef = useRef(null);

  // Role options configuration
  const roleOptions = [
    { value: 'attendee', label: 'Attendee', icon: 'bi-person' },
    { value: 'volunteer', label: 'Volunteer', icon: 'bi-hand-thumbs-up' },
    { value: 'artist', label: 'Artist', icon: 'bi-star' },
    { value: 'invited_guest', label: 'Guest', icon: 'bi-gift' }
  ];

  // Rating categories configuration
  const ratingCategories = [
    { key: 'venue', label: 'Venue', tooltip: 'Quality of the dance floor, air circulation, acoustics, lighting, and overall atmosphere. Was it spacious and comfortable?' },
    { key: 'ratio', label: 'Ratio', tooltip: 'Balance between leads and follows. Were there taxi dancers? Were you able to find dance partners easily throughout the event?' },
    { key: 'organization', label: 'Org', tooltip: 'Event logistics, schedule, communication, registration process, and staff helpfulness.' },
    { key: 'artists', label: 'Artists', tooltip: 'Were DJs and Instructors able to create a rich & consistently enjoyable experience for the attendees?' },
    { key: 'culture', label: 'Culture', tooltip: 'Did it feel like a friendly, inclusive, and respectful community? Were those values encouraged? Were there discussions on dance roots and etiquette?' },
    { key: 'safety', label: 'Safety', tooltip: 'Did it feel like a secure and safe environment? Were there materials or workshops on consent awareness; appointed harassment prevention team members?' }
  ];

  // Swear words filter list
  const swearWords = ['fuck', 'shit', 'ass', 'bitch', 'damn', 'crap', 'bastard', 'dick', 'cock', 'pussy', 'cunt', 'whore', 'slut', 'nigger', 'faggot', 'retard'];

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setSelectedRole('attendee');
      setHideRole(false);
      setReviewText('');
      setEventAmenities({
        codeOfConduct: false,
        freeWater: false,
        freeSnacks: false,
        culturalDiscussions: false,
        switchHour: false,
        socialActivities: false,
      });
      setOrganizerRecommendations('');
      setRatings({ venue: 0, ratio: 0, organization: 0, artists: 0, culture: 0, safety: 0 });
      setShowSuccess(false);
      setShowExtraInfo(false);
      setNameWarningVisible(false);
      setResponsibilityConfirmed(false);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle click outside modal to close
  const handleOverlayClick = (e) => {
    if (e.target === modalRef.current) {
      onClose();
    }
  };

  // Calculate word count
  const getWordCount = useCallback((text) => {
    const trimmed = text.trim();
    if (!trimmed) return 0;
    return trimmed.split(/\s+/).filter(word => word.length > 0).length;
  }, []);

  const wordCount = getWordCount(reviewText);

  // Get word count class for styling
  const getWordCountClass = () => {
    if (wordCount < 20) return 'warning';
    if (wordCount > 100) return 'error';
    return 'valid';
  };

  // Handle star click (supports half-star values)
  const handleStarClick = (category, value) => {
    setRatings(prev => ({ ...prev, [category]: value }));
  };

  // Strip non-alphabetic characters for obfuscation-resistant comparison
  const stripNonAlpha = (str) => str.replace(/[^a-zA-Z\s]/g, '').toLowerCase();

  // Check if review text mentions any artist/individual names from the event
  const containsArtistNames = useCallback((text) => {
    if (!eventArtists.length || !text.trim()) return false;
    const lowerText = text.toLowerCase();
    const strippedText = stripNonAlpha(text);
    return eventArtists.some(artist => {
      const name = artist.trim().toLowerCase();
      if (!name) return false;
      const strippedName = stripNonAlpha(name);
      // Check full name (plain + stripped)
      if (lowerText.includes(name) || strippedText.includes(strippedName)) return true;
      // Check individual parts (first name, last name) — only if 3+ chars to avoid false positives
      const parts = name.split(/\s+/);
      return parts.some(part => {
        const strippedPart = stripNonAlpha(part);
        return strippedPart.length >= 3 && (lowerText.includes(part) || strippedText.includes(strippedPart));
      });
    });
  }, [eventArtists]);

  // Handle review text change with name detection
  const handleReviewTextChange = (text) => {
    setReviewText(text);
    const hasNames = containsArtistNames(text);
    setNameWarningVisible(hasNames);
    if (!hasNames) {
      setResponsibilityConfirmed(false);
    }
  };

  // Check for swear words
  const containsSwearWords = (text) => {
    const lowerText = text.toLowerCase();
    return swearWords.some(word => {
      const regex = new RegExp(`\\b${word}\\b`, 'i');
      return regex.test(lowerText);
    });
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validation
    if (!reviewText.trim()) {
      alert('Please write your review');
      return;
    }

    if (wordCount < 20) {
      alert(`Your review is too short. Please write at least 20 words. (Current: ${wordCount} words)`);
      return;
    }

    if (wordCount > 100) {
      alert(`Your review is too long. Please keep it under 100 words. (Current: ${wordCount} words)`);
      return;
    }

    if (containsSwearWords(reviewText)) {
      alert('Please keep your review respectful and avoid inappropriate language.');
      return;
    }

    // Block submission if names detected and responsibility not confirmed
    if (containsArtistNames(reviewText) && !responsibilityConfirmed) {
      alert('Your review references specific individuals. Please read the notice and confirm the acknowledgment checkbox before submitting.');
      return;
    }

    // Check if all ratings are provided
    const allRatingsProvided = Object.values(ratings).every(r => r > 0);
    if (!allRatingsProvided) {
      alert('Please provide ratings for all categories');
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiBase}/events/feedback/${eventId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: title.trim() || undefined,
          role: selectedRole,
          hideRole,
          description: reviewText.trim(),
          venue: ratings.venue,
          ratio: ratings.ratio,
          organization: ratings.organization,
          artists: ratings.artists,
          culture: ratings.culture,
          safety: ratings.safety,
          eventAmenities,
          organizerRecommendations: organizerRecommendations.trim() || undefined
        })
      });

      const data = await response.json();

      if (data.success) {
        setShowSuccess(true);
        setTimeout(() => {
          onClose();
          onSuccess?.();
        }, 3000);
      } else {
        alert(data.error || data.message || 'Failed to submit review');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render star rating input with half-star support
  const renderStarRating = (category) => {
    const currentRating = ratings[category.key];
    const displayValue = currentRating > 0 ? currentRating.toFixed(1) : '0.0';

    return (
      <div className="rating-category" key={category.key}>
        <div className="label">
          <span className="label-text">{category.label}: <span className="label-value">{displayValue}</span></span>
          <span className="rating-info-icon">
            <i className="bi bi-info-circle"></i>
            <span className="rating-tooltip">{category.tooltip}</span>
          </span>
        </div>
        <StarRating
          rating={currentRating}
          onRatingChange={(value) => handleStarClick(category.key, value)}
          size="medium"
          showValue={false}
          allowHalf={true}
        />
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div
      className={`review-modal-overlay ${isOpen ? 'show' : ''}`}
      ref={modalRef}
      onClick={handleOverlayClick}
    >
      <div className="review-modal">
        <div className="review-modal-header">
          <h3><i className="bi bi-pencil-square"></i> Write a Review</h3>
          <button className="review-modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="review-modal-body">
          {showSuccess ? (
            <div className="review-success-message">
              <i className="bi bi-check-circle-fill"></i>
              <h4>Thank you for sharing your experience with the community!</h4>
              <p>Your review will be editable for a week after submission.</p>
            </div>
          ) : (
          <form onSubmit={(e) => e.preventDefault()}>
            {/* Title */}
            <div className="form-group">
              <label>Title <small>(optional)</small></label>
              <input
                type="text"
                className="form-control-review"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Give your review a catchy title..."
              />
            </div>

            {/* Role Selection */}
            <div className="form-group">
              <label>My role at this event:</label>
              <div className="role-selection">
                {roleOptions.map(role => (
                  <label
                    key={role.value}
                    className={`role-option ${selectedRole === role.value ? 'selected' : ''}`}
                    onClick={() => setSelectedRole(role.value)}
                  >
                    <input
                      type="radio"
                      name="role"
                      value={role.value}
                      checked={selectedRole === role.value}
                      onChange={() => setSelectedRole(role.value)}
                    />
                    <div className="role-icon">
                      <i className={`bi ${role.icon}`}></i>
                    </div>
                    <span className="role-label">{role.label}</span>
                  </label>
                ))}
              </div>
              <label className="hide-role-checkbox">
                <input
                  type="checkbox"
                  checked={hideRole}
                  onChange={(e) => setHideRole(e.target.checked)}
                />
                <span>Hide my role</span>
              </label>
            </div>

            {/* Ratings by Category */}
            <div className="form-group">
              <label>Rate your experience:</label>
              <div className="rating-categories">
                {ratingCategories.map(category => renderStarRating(category))}
              </div>
            </div>

            {/* Review Text */}
            <div className="form-group">
              <label>
                Your Review <span className="required-asterisk">*</span>{' '}
                <small style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(20-100 words)</small>
              </label>
              <textarea
                className="form-control-review"
                value={reviewText}
                onChange={(e) => handleReviewTextChange(e.target.value)}
                rows="4"
                placeholder="Share your experience to help others decide. What made this event special? How was the atmosphere, music, and community? Would you recommend it?"
                required
              />
              <div className={`word-count-display ${getWordCountClass()}`}>
                {wordCount} / 100 words
              </div>

            </div>

            {/* Name detection warning */}
            {nameWarningVisible && (
              <div className="name-warning-box">
                <div className="name-warning-icon">
                  <i className="bi bi-exclamation-triangle-fill"></i>
                </div>
                <div className="name-warning-content">
                  <p className="name-warning-text">
                    It looks like your review may mention a specific person. We encourage reviews to focus on the overall event experience rather than individuals. If you'd like to name someone in relation to a serious concern, please note this is only appropriate when the matter has been formally reported or documented. For your awareness, in the case of a legal inquiry, reviewer details may need to be shared with the relevant authorities. If you have a safety concern you'd like to raise privately, you can use the <button type="button" className="name-warning-link" onClick={onOpenBoycott}>'Boycott an Event'</button> feature, also found in the bottom right corner of the event banner.
                  </p>
                  <label className="name-warning-checkbox">
                    <input
                      type="checkbox"
                      checked={responsibilityConfirmed}
                      onChange={(e) => setResponsibilityConfirmed(e.target.checked)}
                    />
                    <span>My review reflects my personal experience at this event and I take responsibility for its content.</span>
                  </label>
                </div>
              </div>
            )}

            {/* Extra Info - Event Amenities (Expandable) */}
            <div className="form-group">
              <div
                className="extra-info-header"
                onClick={() => setShowExtraInfo(!showExtraInfo)}
              >
                <label>Extra Info <small>(optional)</small></label>
                <i className={`bi ${showExtraInfo ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
              </div>
              {showExtraInfo && (
                <div className="event-amenities">
                  <label className="amenity-checkbox">
                    <input
                      type="checkbox"
                      checked={eventAmenities.codeOfConduct}
                      onChange={(e) => setEventAmenities(prev => ({ ...prev, codeOfConduct: e.target.checked }))}
                    />
                    <span className="amenity-label">
                      <i className="bi bi-shield-check"></i> Code of Conduct
                    </span>
                  </label>
                  <label className="amenity-checkbox">
                    <input
                      type="checkbox"
                      checked={eventAmenities.freeWater}
                      onChange={(e) => setEventAmenities(prev => ({ ...prev, freeWater: e.target.checked }))}
                    />
                    <span className="amenity-label">
                      <i className="bi bi-droplet"></i> Free Water
                    </span>
                  </label>
                  <label className="amenity-checkbox">
                    <input
                      type="checkbox"
                      checked={eventAmenities.freeSnacks}
                      onChange={(e) => setEventAmenities(prev => ({ ...prev, freeSnacks: e.target.checked }))}
                    />
                    <span className="amenity-label">
                      <i className="bi bi-cup-hot"></i> Free Snacks
                    </span>
                  </label>
                  <label className="amenity-checkbox">
                    <input
                      type="checkbox"
                      checked={eventAmenities.culturalDiscussions}
                      onChange={(e) => setEventAmenities(prev => ({ ...prev, culturalDiscussions: e.target.checked }))}
                    />
                    <span className="amenity-label">
                      <i className="bi bi-chat-heart"></i> Cultural Discussions
                    </span>
                  </label>
                  <label className="amenity-checkbox">
                    <input
                      type="checkbox"
                      checked={eventAmenities.switchHour}
                      onChange={(e) => setEventAmenities(prev => ({ ...prev, switchHour: e.target.checked }))}
                    />
                    <span className="amenity-label">
                      <i className="bi bi-arrow-repeat"></i> Switch Hour
                    </span>
                  </label>
                  <label className="amenity-checkbox">
                    <input
                      type="checkbox"
                      checked={eventAmenities.socialActivities}
                      onChange={(e) => setEventAmenities(prev => ({ ...prev, socialActivities: e.target.checked }))}
                    />
                    <span className="amenity-label">
                      <i className="bi bi-people"></i> Social Activities
                    </span>
                  </label>
                </div>
              )}
            </div>

            {/* Organizer Recommendations */}
            <div className="form-group">
              <div className="organizer-recommendations">
                <label>Recommendations for organizers <small>(optional)</small></label>
                <textarea
                  className="form-control-review"
                  value={organizerRecommendations}
                  onChange={(e) => setOrganizerRecommendations(e.target.value)}
                  rows="3"
                  placeholder="These invaluable suggestions of yours won't be publicly shown but shared with organizers who express interest in the feedback of this kind."
                />
                <p className="hint"><i className="bi bi-lock"></i> Private - only shared with organizers</p>
              </div>
            </div>
          </form>
          )}
        </div>

        {!showSuccess && (
        <div className="review-modal-footer">
          <button className="btn-cancel-review" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-submit-review"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <i className="bi bi-hourglass"></i> Submitting...
              </>
            ) : (
              <>
                <i className="bi bi-send"></i> Submit Review
              </>
            )}
          </button>
        </div>
        )}
      </div>
    </div>
  );
};

export default WriteReviewModal;
